// src/components/dashboard/ListeProblemesSheet.tsx — SCE Issues
// Les problèmes derrière un chiffre, dans un panneau : « À qualifier » de
// l'accueil, « Voir les N problèmes » du dashboard, problèmes suivis…
//
// Refonte du 30/09 (maquette validée) : les cartes de la V1 (112 px chacune,
// deux bulles identiques sur chaque ligne) laissent place à des rangées denses.
//  - Bureau : recherche, bouton de tri, pastilles de filtre, colonnes
//    Problème / Machine / Âge (ou Statut), chevron.
//  - Téléphone : pastilles + bouton de tri, rangées de 64 px.
//  - La colonne de droite ne montre que ce qui distingue les lignes DANS cette
//    liste : l'âge pour « À qualifier » (`colonne: 'age'`), le statut et la
//    criticité pour les listes mixtes (`'statut'`, défaut).
//  - Tri en menu, « par machine » regroupe (un même équipement qui revient
//    saute aux yeux). La colonne Machine, devenue redondante avec le titre du
//    groupe, cède la place à « Métier » : c'est là qu'on voit qu'une machine
//    a des problèmes dans plusieurs métiers.
//
// Le dashboard reste monté dessous : au-delà de 768px, la fiche d'un problème
// s'ouvre par-dessus ce panneau et le retrouve à la fermeture.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { ArrowDownUp, Check, ChevronRight, Search, X } from 'lucide-react'
import { PastilleAge, LigneProbleme } from '@/components/accueil/LigneProbleme'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { SuiteListe } from '@/components/layout/SuiteProblemes'
import { METIERS_COULEURS, METIERS_LABELS_COURT } from '@/config/metiers.config'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { useMaintenant } from '@/hooks/useMaintenant'
import type { Issue } from '@/types/issue.types'
import { JOURS_SANS_ACTIVITE, joursDepuis, libelleAge } from '@/utils/accueil'
import {
  LIBELLE_TRI,
  LIBELLE_TRI_COURT,
  SOUS_TITRE_TRI,
  filtrer,
  libelleNombre,
  optionsFiltre,
  organiser,
  triParDefaut,
  trisProposes,
  type ColonneListe,
  type TriListe,
} from '@/utils/listeProblemes'
import { lienProbleme } from '@/utils/liensFiches'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { PastilleNcs } from '@/components/issues/PastilleNcs'

export interface ListeProblemes {
  titre: string
  /** Contexte affiché sous le titre : « MECA », « Colonne › Hipersep… »… */
  contexte?: string
  issues: Issue[]
  /**
   * Ce que montre la colonne de droite. `age` quand tout est à qualifier
   * (le statut et la criticité seraient identiques partout) ; `statut` sinon.
   */
  colonne?: ColonneListe
}

interface ListeProblemesSheetProps {
  liste: ListeProblemes
  onClose: () => void
}

/** Au-delà, la recherche s'affiche aussi au téléphone. */
const SEUIL_RECHERCHE_TELEPHONE = 10

export function ListeProblemesSheet({ liste, onClose }: ListeProblemesSheetProps): JSX.Element {
  const colonne = liste.colonne ?? 'statut'
  const estBureau = useEstBureau()
  const maintenant = useMaintenant()
  const [filtre, setFiltre] = useState<string | null>(null)
  const [tri, setTri] = useState<TriListe>(triParDefaut(colonne))
  const [texte, setTexte] = useState('')
  const [menu, setMenu] = useState(false)
  const barreRef = useRef<HTMLDivElement>(null)

  const pastilles = useMemo(
    () => optionsFiltre(liste.issues, colonne, (m) => METIERS_LABELS_COURT[m]),
    [liste.issues, colonne],
  )
  const affiches = useMemo(() => filtrer(liste.issues, colonne, filtre, texte), [liste.issues, colonne, filtre, texte])
  const entrees = useMemo(() => organiser(affiches, tri), [affiches, tri])
  // Ce que la fiche parcourt (« Problème suivant ») : les lignes dans l'ordre
  // affiché — tri, pastille et recherche compris, sans les titres de groupe.
  const lignes = useMemo(
    () => entrees.flatMap((e) => (e.type === 'groupe' ? [] : [e.issue])),
    [entrees],
  )
  const avecRecherche = estBureau || liste.issues.length > SEUIL_RECHERCHE_TELEPHONE

  // Échap ferme d'abord le menu de tri, puis la fenêtre.
  useEffect(() => {
    function surTouche(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return
      if (menu) setMenu(false)
      else onClose()
    }
    function surClic(e: MouseEvent): void {
      if (menu && barreRef.current && !barreRef.current.contains(e.target as Node)) setMenu(false)
    }
    window.addEventListener('keydown', surTouche)
    document.addEventListener('mousedown', surClic)
    return () => {
      window.removeEventListener('keydown', surTouche)
      document.removeEventListener('mousedown', surClic)
    }
  }, [menu, onClose])

  const sousTitre = [liste.contexte, SOUS_TITRE_TRI[tri]].filter(Boolean).join(' · ')
  const colonnes = colonne === 'age' ? '4px minmax(0, 1fr) 96px 64px 16px' : '4px minmax(0, 1fr) 96px 170px 16px'
  const styleColonnes = { '--colonnes': colonnes } as CSSProperties

  const boutonTri = (
    <button
      type="button"
      className={estBureau ? 'liste-tri liste-tri--texte' : 'liste-tri'}
      aria-label="Trier"
      aria-haspopup="menu"
      aria-expanded={menu}
      onClick={() => setMenu((v) => !v)}
    >
      <ArrowDownUp size={18} aria-hidden="true" />
      {estBureau && LIBELLE_TRI_COURT[tri]}
    </button>
  )

  const listeTris = menu && (
    <div className="liste-menu" role="menu" aria-label="Trier">
      {trisProposes(colonne).map((t) => (
        <button
          key={t}
          type="button"
          role="menuitemradio"
          aria-checked={tri === t}
          className="liste-option"
          onClick={() => {
            setTri(t)
            setMenu(false)
          }}
        >
          <span>{LIBELLE_TRI[t]}</span>
          {tri === t && <Check size={18} aria-hidden="true" />}
        </button>
      ))}
    </div>
  )

  const rangeePastilles = pastilles && (
    <div className="flex" style={{ gap: 8, overflowX: 'auto', flexGrow: 1, minWidth: 0, scrollbarWidth: 'none' }}>
      <Pastille libelle="Tous" n={liste.issues.length} actif={filtre === null} onClick={() => setFiltre(null)} />
      {pastilles.map((o) => (
        <Pastille
          key={o.cle}
          libelle={o.libelle}
          n={o.n}
          couleur={o.metier ? METIERS_COULEURS[o.metier] : undefined}
          actif={filtre === o.cle}
          onClick={() => setFiltre(filtre === o.cle ? null : o.cle)}
        />
      ))}
    </div>
  )

  const champRecherche = (
    <label className="liste-recherche">
      <Search size={16} color="var(--color-gray-50)" aria-hidden="true" />
      <input
        type="search"
        value={texte}
        onChange={(e) => setTexte(e.target.value)}
        placeholder="Filtrer : titre, équipement, déclarant…"
        aria-label="Filtrer la liste"
      />
    </label>
  )

  return (
    <div className="sheet-overlay" onClick={onClose} role="presentation">
      <div
        className="sheet-panel liste-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={liste.titre}
      >
        {/* En-tête */}
        <div
          className="flex items-center justify-between"
          style={{ padding: estBureau ? '14px 12px 0 24px' : '12px 8px 0 16px', flexShrink: 0 }}
        >
          <div className="min-w-0">
            <h3 style={{ margin: 0, fontSize: 21, lineHeight: '28px', fontWeight: 500 }}>
              {liste.titre}{' '}
              <span style={{ color: 'var(--color-gray-70)', fontWeight: 400 }}>· {affiches.length}</span>
            </h3>
            <div style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>{sousTitre}</div>
          </div>
          <button type="button" className="liste-icone" onClick={onClose} aria-label="Fermer">
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Outils : recherche, pastilles, tri */}
        <div
          ref={barreRef}
          style={{
            position: 'relative',
            flexShrink: 0,
            padding: estBureau ? '12px 24px 14px' : '10px 16px 12px',
            borderBottom: estBureau ? undefined : '1px solid var(--color-border)',
          }}
        >
          {estBureau ? (
            <div className="flex flex-col" style={{ gap: 12 }}>
              <div className="flex items-center" style={{ gap: 12 }}>
                {champRecherche}
                {boutonTri}
              </div>
              {rangeePastilles}
            </div>
          ) : (
            <div className="flex flex-col" style={{ gap: 10 }}>
              {avecRecherche && champRecherche}
              <div className="flex items-center" style={{ gap: 8 }}>
                {rangeePastilles ?? <div style={{ flexGrow: 1 }} />}
                {boutonTri}
              </div>
            </div>
          )}
          {listeTris}
        </div>

        {/* Bureau : titres de colonnes */}
        {estBureau && (
          <div className="liste-entete" style={styleColonnes}>
            <span />
            <span style={{ paddingLeft: 10 }}>Problème</span>
            <span>{tri === 'machine' ? 'Métier' : 'Équipement'}</span>
            <span>{colonne === 'age' ? 'Âge' : 'Statut'}</span>
            <span />
          </div>
        )}

        {/* Rangées */}
        <SuiteListe libelle={liste.titre} issues={lignes}>
          <div style={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', paddingBottom: estBureau ? 0 : 24 }}>
            {entrees.length === 0 && (
              <p style={{ margin: 0, padding: 24, fontSize: 14, color: 'var(--color-gray-70)' }}>
                {liste.issues.length === 0 ? 'Aucun problème.' : 'Aucun problème pour ces critères.'}
              </p>
            )}
            {entrees.map((e) =>
              e.type === 'groupe' ? (
                <div key={`g:${e.cle}`} className="liste-groupe">
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{e.machine}</span>
                  <span style={{ fontSize: 12, color: 'var(--color-gray-70)' }}>{libelleNombre(e.n)}</span>
                </div>
              ) : estBureau ? (
                <RangeeBureau
                  key={e.issue.id}
                  issue={e.issue}
                  colonne={colonne}
                  maintenant={maintenant}
                  style={styleColonnes}
                  regroupe={tri === 'machine'}
                />
              ) : (
                <RangeeTelephone
                  key={e.issue.id}
                  issue={e.issue}
                  colonne={colonne}
                  maintenant={maintenant}
                  avecMachine={tri !== 'machine'}
                />
              ),
            )}
          </div>
        </SuiteListe>
      </div>
    </div>
  )
}

// ─── Morceaux ───

function Pastille({
  libelle,
  n,
  couleur,
  actif,
  onClick,
}: {
  libelle: string
  n: number
  couleur?: string
  actif: boolean
  onClick: () => void
}): JSX.Element {
  return (
    <button type="button" className="liste-chip" aria-pressed={actif} onClick={onClick}>
      {couleur && (
        <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: couleur }} />
      )}
      {libelle} <span className="liste-chip-n">{n}</span>
    </button>
  )
}

/** Ce qu'on lit à droite : l'âge d'une attente, ou le statut et la criticité. */
function Droite({
  issue,
  colonne,
  maintenant,
}: {
  issue: Issue
  colonne: ColonneListe
  maintenant: number
}): JSX.Element {
  if (colonne === 'age') {
    return (
      <PastilleAge
        texte={libelleAge(issue.dateDeclaration, maintenant)}
        enRetard={joursDepuis(issue.dateDeclaration, maintenant) >= JOURS_SANS_ACTIVITE}
      />
    )
  }
  return (
<EtatProbleme statut={issue.statut} criticite={issue.criticite} />
  )
}

function RangeeTelephone({
  issue,
  colonne,
  maintenant,
  avecMachine,
}: {
  issue: Issue
  colonne: ColonneListe
  maintenant: number
  avecMachine: boolean
}): JSX.Element {
  return (
    <LigneProbleme
      issue={issue}
      deuxLignes
      meta={[issue.id, avecMachine ? issue.numeroSerie : '', issue.declarantNom].filter(Boolean).join(' · ')}
      droite={<Droite issue={issue} colonne={colonne} maintenant={maintenant} />}
    />
  )
}

function RangeeBureau({
  issue,
  colonne,
  maintenant,
  style,
  regroupe,
}: {
  issue: Issue
  colonne: ColonneListe
  maintenant: number
  style: CSSProperties
  /** Trié par machine : la colonne Machine devient « Métier ». */
  regroupe: boolean
}): JSX.Element {
  const ouvrirFiche = useOuvrirFiche()
  return (
    <button type="button" className="liste-ligne" style={style} onClick={() => ouvrirFiche(lienProbleme(issue.id))}>
      <span
        style={{ alignSelf: 'stretch', backgroundColor: METIERS_COULEURS[issue.metier] }}
        title={METIERS_LABELS_COURT[issue.metier]}
      >
        <span className="sr-only">{METIERS_LABELS_COURT[issue.metier]}</span>
      </span>
      <span className="flex flex-col min-w-0" style={{ gap: 2, padding: '8px 0 8px 10px' }}>
        <span
          style={{ fontSize: 15, lineHeight: '19px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {issue.titre}
        </span>
        <span
          style={{ fontSize: 12, color: 'var(--color-gray-70)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <PastilleNcs issue={issue} />
          {[issue.id, issue.declarantNom].filter(Boolean).join(' · ')}
        </span>
      </span>
      {regroupe ? (
        <span className="flex items-center" style={{ gap: 6, fontSize: 13 }}>
          <span
            aria-hidden="true"
            style={{ width: 10, height: 10, borderRadius: 5, flexShrink: 0, backgroundColor: METIERS_COULEURS[issue.metier] }}
          />
          {METIERS_LABELS_COURT[issue.metier]}
        </span>
      ) : (
        <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{issue.numeroSerie}</span>
      )}
      <span style={{ justifySelf: 'start' }}>
        <Droite issue={issue} colonne={colonne} maintenant={maintenant} />
      </span>
      <ChevronRight className="liste-chevron" size={16} aria-hidden="true" />
    </button>
  )
}
